import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { api, authState } from '../state/auth.js';
import { useAuth } from '../hooks/useAuth.js';
import Card from '../components/shared/Card.jsx';
import Button from '../components/shared/Button.jsx';
import { parseServerDate } from '../utils/dates.js';
import { PushSettingsCard } from '../components/shared/PushSettings.jsx';
import ThanksCard from '../components/shared/ThanksCard.jsx';
import HandoverDraftCard from '../components/shared/HandoverDraftCard.jsx';
import { hasModule } from '../utils/plan.js';
import PasswordCard from '../components/shared/PasswordCard.jsx';
import MfaCard from '../components/shared/MfaCard.jsx';
import { useAway, AwayBadge } from '../utils/away.js';
import AiAssist from '../components/shared/AiAssist.jsx';
import { fileLinkClick } from '../utils/openFile.js';

// ── Helpers ───────────────────────────────────────────────────────────────────

const AVAILABILITY = {
  beschikbaar:      { label: 'Beschikbaar',         color: '#16a34a', bg: '#dcfce7' },
  beperkt:          { label: 'Beperkt beschikbaar',  color: '#d97706', bg: '#fef3c7' },
  niet_beschikbaar: { label: 'Niet beschikbaar',     color: '#dc2626', bg: '#fee2e2' },
};

function avatarColor(name) {
  const colors = ['#6366f1','#8b5cf6','#ec4899','#f59e0b','#10b981','#3b82f6','#ef4444'];
  let h = 0;
  for (const c of (name || '')) h = (h * 31 + c.charCodeAt(0)) & 0xffff;
  return colors[h % colors.length];
}

function initials(name) {
  return (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

function formatDate(str) {
  if (!str) return '—';
  return parseServerDate(str).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
}

// Local "YYYY-MM-DD", built from parts so Safari reads it the same way
function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function isExpired(dateStr) {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date();
}

function isExpiringSoon(dateStr) {
  if (!dateStr) return false;
  const diff = new Date(dateStr) - new Date();
  return diff > 0 && diff < 60 * 24 * 60 * 60 * 1000; // within 60 days
}

// ── Availability Badge ────────────────────────────────────────────────────────

function AvailabilityBadge({ value }) {
  const meta = AVAILABILITY[value] || AVAILABILITY.beschikbaar;
  return (
    <span style={`display:inline-block;padding:3px 10px;border-radius:12px;font-size:12px;font-weight:500;background:${meta.bg};color:${meta.color}`}>
      {meta.label}
    </span>
  );
}

// ── Certificate Form (add + edit) ─────────────────────────────────────────────

const STATUS_LABELS = { volgend: 'Volgend', behaald: 'Behaald', niet_behaald: 'Niet behaald' };
const STATUS_COLORS = {
  volgend:      { bg: '#eff6ff', border: '#93c5fd', color: '#1d4ed8' },
  behaald:      { bg: '#f0fdf4', border: '#86efac', color: '#15803d' },
  niet_behaald: { bg: '#fef2f2', border: '#fca5a5', color: '#b91c1c' },
};

function CertificateForm({ onSave, onCancel, saving, initial }) {
  const defaultStatus = initial?.status || 'behaald';
  const [form, setForm] = useState({
    type_id: initial ? String(initial.type_id || '') : '',
    name: initial?.name || '',
    issued_by: initial?.issued_by || '',
    issued_date: initial?.issued_date || '',
    expires_date: initial?.expires_date || '',
    status: defaultStatus,
  });
  const [types, setTypes] = useState(null);
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  useEffect(() => {
    api('/api/profiles/certificate-types')
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        setTypes(list);
        if (!initial && list.length > 0) {
          setForm(f => ({ ...f, type_id: String(list[0].id), name: list[0].name }));
        }
      })
      .catch(() => setTypes([]));
  }, []);

  function handleTypeChange(e) {
    const id = e.target.value;
    const found = types?.find(t => String(t.id) === id);
    setForm(f => ({ ...f, type_id: id, name: found ? found.name : f.name }));
  }

  if (types === null) return <div style="padding:12px;font-size:13px;color:var(--color-text-muted)">Laden…</div>;

  if (!initial && types.length === 0) return (
    <div style="padding:12px;background:var(--color-bg-secondary);border-radius:8px;border:1px solid var(--color-border);margin-top:10px;font-size:13px;color:var(--color-text-muted)">
      Nog geen certificaattypen beschikbaar. Een TC-beheerder kan typen aanmaken via de Kwalificaties module.
      <div style="margin-top:8px"><Button variant="secondary" size="sm" onClick={onCancel}>Annuleren</Button></div>
    </div>
  );

  const selectedType = types?.find(t => String(t.id) === String(form.type_id));
  const typeRequiresExpiry = selectedType ? selectedType.requires_expiry === 1 : true;
  const DATE_LABELS = {
    volgend:      { d1: 'Startdatum cursus', d2: 'Verwachte behaaldatum' },
    behaald:      { d1: 'Datum behaald',     d2: 'Vervaldatum' },
    niet_behaald: { d1: 'Datum',             d2: null },
  };
  const dateLabels = DATE_LABELS[form.status] || DATE_LABELS.behaald;
  const showSecondDate = dateLabels.d2 && (form.status !== 'behaald' || typeRequiresExpiry);

  return (
    <div style="display:flex;flex-direction:column;gap:8px;padding:12px;background:var(--color-bg-secondary);border-radius:8px;border:1px solid var(--color-border);margin-top:6px">
      <div>
        <label style="font-size:11px;color:var(--color-text-muted);display:block;margin-bottom:3px">Status *</label>
        <div style="display:flex;gap:6px;flex-wrap:wrap">
          {Object.entries(STATUS_LABELS).map(([val, label]) => {
            const c = STATUS_COLORS[val];
            const active = form.status === val;
            return (
              <button
                key={val}
                type="button"
                onClick={() => set('status', val)}
                style={`font-size:12px;font-weight:600;padding:4px 12px;border-radius:20px;cursor:pointer;border:1px solid ${active ? c.border : 'var(--color-border)'};background:${active ? c.bg : 'transparent'};color:${active ? c.color : 'var(--color-text-muted)'}`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
      {types.length > 0 && (
        <div>
          <label style="font-size:11px;color:var(--color-text-muted);display:block;margin-bottom:3px">Type *</label>
          <select class="form-input" style="font-size:13px" value={form.type_id} onChange={handleTypeChange}>
            {!form.type_id && <option value="">Kies een type…</option>}
            {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
      )}
      {form.status === 'behaald' && (
        <input
          class="form-input"
          style="font-size:13px"
          placeholder="Uitgegeven door (bijv. KNVB, SJO)"
          value={form.issued_by}
          onInput={e => set('issued_by', e.target.value)}
        />
      )}
      <div style={`display:grid;grid-template-columns:${showSecondDate ? '1fr 1fr' : '1fr'};gap:8px`}>
        <div>
          <label style="font-size:11px;color:var(--color-text-muted);display:block;margin-bottom:3px">{dateLabels.d1}</label>
          <input class="form-input" style="font-size:13px" type="date" value={form.issued_date} onInput={e => set('issued_date', e.target.value)} />
        </div>
        {showSecondDate && (
          <div>
            <label style="font-size:11px;color:var(--color-text-muted);display:block;margin-bottom:3px">{dateLabels.d2}</label>
            <input class="form-input" style="font-size:13px" type="date" value={form.expires_date} onInput={e => set('expires_date', e.target.value)} />
          </div>
        )}
      </div>
      <div style="display:flex;gap:6px;margin-top:2px">
        <Button variant="primary" size="sm" onClick={() => onSave(form)} disabled={saving || !form.type_id}>
          {saving ? '…' : (initial ? 'Opslaan' : 'Toevoegen')}
        </Button>
        <Button variant="secondary" size="sm" onClick={onCancel}>Annuleren</Button>
      </div>
    </div>
  );
}

// ── Delegation Section ────────────────────────────────────────────────────────

function DelegationSection({ targetId, canEdit, onChange }) {
  const [delegation, setDelegation] = useState(undefined); // undefined = loading
  const [members, setMembers] = useState([]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ to_user_id: '', starts_at: '', ends_at: '', reason: '' });
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([
      api(`/api/delegatie/user/${targetId}`).catch(() => null),
      canEdit ? api('/api/delegatie/members').catch(() => []) : Promise.resolve([]),
    ]).then(([del, mems]) => {
      setDelegation(del);
      setMembers(mems || []);
    });
  }, [targetId]);

  function startEdit() {
    setForm({
      to_user_id: delegation?.to_user_id || '',
      starts_at: delegation?.starts_at || '',
      ends_at: delegation?.ends_at || '',
      reason: delegation?.reason || '',
    });
    setEditing(true);
    setError(null);
  }

  async function handleSave() {
    if (!form.to_user_id) { setError('Selecteer een vervanger.'); return; }
    setSaving(true);
    setError(null);
    try {
      const body = { to_user_id: parseInt(form.to_user_id), reason: form.reason || null };
      if (form.starts_at) body.starts_at = form.starts_at;
      if (form.ends_at) body.ends_at = form.ends_at;
      const result = await api('/api/delegatie', { method: 'POST', body: JSON.stringify(body) });
      setDelegation(result);
      setEditing(false);
      onChange?.();
    } catch (err) {
      setError({
        end_before_start: 'De einddatum ligt voor de startdatum.',
        invalid_date: 'Vul geldige datums in.',
        delegate_not_in_club: 'Kies een actief lid van de club als vervanger.',
        invalid_reason: 'De reden is te lang (max. 500 tekens).',
      }[err.code] || 'Opslaan mislukt. Probeer opnieuw.');
    }
    setSaving(false);
  }

  async function handleDelete() {
    if (!delegation) return;
    setSaving(true);
    try {
      await api(`/api/delegatie/${delegation.id}`, { method: 'DELETE' });
      setDelegation(null);
      onChange?.();
    } catch {}
    setSaving(false);
  }

  if (delegation === undefined) return null;

  return (
    <Card title="🔄 Vakantiedelegatie">
      {!editing && delegation && (
        <div>
          <div style="padding:10px 12px;border-radius:8px;border:1px solid var(--color-primary);background:var(--color-bg-secondary);margin-bottom:10px">
            <div style="font-size:13px;font-weight:600;margin-bottom:4px">
              {delegation.running ? '🌴 Loopt nu' : '📅 Gepland'} → {delegation.to_name}
            </div>
            {(delegation.starts_at || delegation.ends_at) && (
              <div style="font-size:12px;color:var(--color-text-muted);margin-bottom:3px">
                {delegation.starts_at && `Vanaf: ${formatDate(delegation.starts_at)}`}
                {delegation.starts_at && delegation.ends_at && ' · '}
                {delegation.ends_at && `t/m: ${formatDate(delegation.ends_at)}`}
              </div>
            )}
            {delegation.reason && (
              <div style="font-size:12px;color:var(--color-text-muted)">{delegation.reason}</div>
            )}
            <div style="font-size:12px;color:var(--color-text-muted);margin-top:6px">
              {delegation.running
                ? `${delegation.to_name.split(' ')[0]} werkt mee aan de open taken, krijgt een kopie van de werkmeldingen en de coins voor taken die hij afrondt. Anderen zien dat je afwezig bent.`
                : `Vanaf de startdatum werkt ${delegation.to_name.split(' ')[0]} mee aan de open taken en krijgt een kopie van de werkmeldingen.`}
            </div>
          </div>
          {canEdit && (
            <div style="display:flex;gap:6px">
              <Button variant="secondary" size="sm" onClick={startEdit}>Wijzigen</Button>
              <Button variant="danger" size="sm" onClick={handleDelete} disabled={saving}>Verwijderen</Button>
            </div>
          )}
        </div>
      )}

      {!editing && !delegation && (
        <div>
          <p class="text-muted text-sm" style="margin-bottom:10px">
            {canEdit
              ? 'Ben je een tijd weg? Kies een vervanger: die werkt in die periode mee aan je open taken en krijgt een kopie van je werkmeldingen.'
              : 'Geen actieve delegatie ingesteld.'}
          </p>
          {canEdit && (
            <button
              onClick={startEdit}
              style="background:none;border:1px dashed var(--color-border);border-radius:8px;width:100%;padding:8px;color:var(--color-text-muted);font-size:13px;cursor:pointer;text-align:center"
            >
              + Delegatie instellen
            </button>
          )}
        </div>
      )}

      {editing && (
        <div style="display:flex;flex-direction:column;gap:8px">
          <div>
            <label style="font-size:11px;color:var(--color-text-muted);display:block;margin-bottom:3px">Vervanger *</label>
            <select
              class="form-select"
              style="font-size:13px"
              value={form.to_user_id}
              onChange={e => setForm(f => ({ ...f, to_user_id: e.target.value }))}
            >
              <option value="">Selecteer een lid…</option>
              {members.map(m => (
                <option key={m.id} value={m.id}>{m.display_name}</option>
              ))}
            </select>
          </div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px">
            <div>
              <label style="font-size:11px;color:var(--color-text-muted);display:block;margin-bottom:3px">Ingangsdatum (optioneel)</label>
              <input class="form-input" style="font-size:13px" type="date" value={form.starts_at} onInput={e => setForm(f => ({ ...f, starts_at: e.target.value }))} />
            </div>
            <div>
              <label style="font-size:11px;color:var(--color-text-muted);display:block;margin-bottom:3px">Einddatum (optioneel)</label>
              <input class="form-input" style="font-size:13px" type="date" value={form.ends_at} onInput={e => setForm(f => ({ ...f, ends_at: e.target.value }))} />
            </div>
          </div>
          <div>
            <label style="font-size:11px;color:var(--color-text-muted);display:block;margin-bottom:3px">Reden (optioneel)</label>
            <input class="form-input" style="font-size:13px" placeholder="Bijv. vakantie, ziekte…" value={form.reason} onInput={e => setForm(f => ({ ...f, reason: e.target.value }))} />
          </div>
          {error && <div style="font-size:12px;color:#dc2626">{error}</div>}
          <div style="display:flex;gap:6px;margin-top:2px">
            <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
              {saving ? '…' : 'Opslaan'}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => { setEditing(false); setError(null); }}>Annuleren</Button>
          </div>
        </div>
      )}
    </Card>
  );
}

// ── Main View ─────────────────────────────────────────────────────────────────

export default function ProfileView({ userId: userIdProp }) {
  const auth = useAuth();
  const currentUserId = Number(auth.userId);

  // If no userId prop → show own profile
  const targetId = userIdProp ? parseInt(userIdProp) : currentUserId;
  const [awayKey, setAwayKey] = useState(0);
  const away = useAway(`${targetId}:${awayKey}`);
  const isOwnProfile = targetId === currentUserId;
  const isAdmin = auth.isPlatformAdmin || auth.hasPermission('settings', 'update');
  const canEdit = isOwnProfile || isAdmin;

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Availability edit
  const [editingAvail, setEditingAvail] = useState(false);
  const [availValue, setAvailValue] = useState('beschikbaar');
  const [savingAvail, setSavingAvail] = useState(false);

  // Bio edit (only own profile)
  const [editingBio, setEditingBio] = useState(false);
  const [bioForm, setBioForm] = useState({ bio: '', contact_for: '', fun_fact: '', birth_date: '' });
  const [savingBio, setSavingBio] = useState(false);
  const [bioError, setBioError] = useState(null);

  // Certificates
  const [showCertForm, setShowCertForm] = useState(false);
  const [savingCert, setSavingCert] = useState(false);
  const [deletingCertId, setDeletingCertId] = useState(null);
  const [editingCertId, setEditingCertId] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api(`/api/profiles/${targetId}`)
      .then(data => {
        setProfile(data);
        setAvailValue(data.availability || 'beschikbaar');
        setBioForm({ bio: data.bio || '', contact_for: data.contact_for || '', fun_fact: data.fun_fact || '', birth_date: data.birth_date || '' });
      })
      .catch(() => setError('Profiel kon niet worden geladen.'))
      .finally(() => setLoading(false));
  }, [targetId]);

  async function handleSaveAvailability() {
    setSavingAvail(true);
    try {
      await api(`/api/profiles/${targetId}/availability`, {
        method: 'PUT',
        body: JSON.stringify({ availability: availValue }),
      });
      setProfile(p => ({ ...p, availability: availValue }));
      setEditingAvail(false);
    } catch {}
    setSavingAvail(false);
  }

  async function handleSaveBio() {
    setSavingBio(true);
    setBioError(null);
    try {
      await api(`/api/profiles/${targetId}/bio`, {
        method: 'PUT',
        body: JSON.stringify(bioForm),
      });
      setProfile(p => ({ ...p, ...bioForm, birth_date: bioForm.birth_date || null }));
      setEditingBio(false);
    } catch (e) {
      setBioError(e.code === 'invalid_birth_date'
        ? 'Vul een geldige geboortedatum in.'
        : 'Opslaan is niet gelukt. Probeer het opnieuw.');
    }
    setSavingBio(false);
  }

  async function handleAddCertificate(form) {
    setSavingCert(true);
    try {
      const cert = await api(`/api/profiles/${targetId}/certificates`, {
        method: 'POST',
        body: JSON.stringify(form),
      });
      setProfile(p => ({ ...p, certificates: [...(p.certificates || []), cert] }));
      setShowCertForm(false);
    } catch {}
    setSavingCert(false);
  }

  async function handleDeleteCertificate(certId) {
    setDeletingCertId(certId);
    try {
      await api(`/api/profiles/certificates/${certId}`, { method: 'DELETE' });
      setProfile(p => ({ ...p, certificates: p.certificates.filter(c => c.id !== certId) }));
    } catch {}
    setDeletingCertId(null);
  }

  async function handleUploadCertFile(certId, e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      alert('Bestand is te groot (max 10 MB).');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`/api/profiles/certificates/${certId}/file`, { method: 'POST', body: fd });
      if (res.status === 413) { alert('Bestand is te groot. Verkleijn het PDF-bestand en probeer opnieuw.'); return; }
      if (!res.ok) { alert('Upload mislukt. Probeer opnieuw.'); return; }
      const data = await res.json();
      if (data.file_path) {
        setProfile(p => ({ ...p, certificates: p.certificates.map(c => c.id === certId ? { ...c, file_path: data.file_path } : c) }));
      }
    } catch {
      alert('Upload mislukt. Controleer je verbinding en probeer opnieuw.');
    }
  }

  async function handleDeleteCertFile(certId) {
    try {
      await api(`/api/profiles/certificates/${certId}/file`, { method: 'DELETE' });
      setProfile(p => ({ ...p, certificates: p.certificates.map(c => c.id === certId ? { ...c, file_path: null } : c) }));
    } catch {}
  }

  async function handleEditCertificate(certId, form) {
    setSavingCert(true);
    try {
      const updated = await api(`/api/profiles/certificates/${certId}`, {
        method: 'PUT',
        body: JSON.stringify(form),
      });
      setProfile(p => ({ ...p, certificates: p.certificates.map(c => c.id === certId ? { ...c, ...updated } : c) }));
      setEditingCertId(null);
    } catch {}
    setSavingCert(false);
  }

  if (loading) {
    return (
      <div>
        <div class="page-header"><h1 class="page-title">Profiel</h1></div>
        <div class="text-muted text-center" style="padding:60px">Laden…</div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div>
        <div class="page-header"><h1 class="page-title">Profiel</h1></div>
        <div class="alert alert-error mt-4">{error || 'Niet gevonden'}</div>
      </div>
    );
  }

  const color = avatarColor(profile.display_name);
  const certs = profile.certificates || [];

  async function handleAvatarUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append('avatar', file);
    try {
      const res = await fetch(`/api/profiles/${targetId}/avatar`, { method: 'POST', body: form });
      const data = await res.json();
      if (data.avatar_url) {
        setProfile(p => ({ ...p, avatar_url: data.avatar_url }));
        // Update authState so header/sidebar update immediately
        authState.value = { ...authState.value, avatarUrl: data.avatar_url };
      }
    } catch {}
  }

  async function handleAvatarDelete() {
    try {
      await fetch(`/api/profiles/${targetId}/avatar`, { method: 'DELETE' });
      setProfile(p => ({ ...p, avatar_url: null }));
      authState.value = { ...authState.value, avatarUrl: null };
    } catch {}
  }

  return (
    <div>
      <div class="page-header">
        <h1 class="page-title">{isOwnProfile ? 'Mijn profiel' : 'Ledenprofiel'}</h1>
      </div>

      <div class="grid-2 mt-4" style="align-items:start">

        {/* Left column — identity + availability + sections + roles */}
        <div style="display:flex;flex-direction:column;gap:16px">

          {/* Identity card */}
          <Card>
            <div style="display:flex;align-items:center;gap:16px;margin-bottom:16px">
              <div style="position:relative;flex-shrink:0">
                <div style={`width:64px;height:64px;border-radius:50%;background:${color};display:flex;align-items:center;justify-content:center;font-size:24px;font-weight:700;color:#fff;overflow:hidden`}>
                  {profile.avatar_url
                    ? <img src={profile.avatar_url} alt="" style="width:100%;height:100%;object-fit:cover;display:block" />
                    : initials(profile.display_name)
                  }
                </div>
                {isOwnProfile && (
                  <label title="Foto uploaden" style="position:absolute;bottom:0;right:0;width:22px;height:22px;border-radius:50%;background:var(--color-primary);display:flex;align-items:center;justify-content:center;cursor:pointer;font-size:11px;color:#fff;border:2px solid var(--color-surface)">
                    📷
                    <input type="file" accept="image/*" style="display:none" onChange={handleAvatarUpload} />
                  </label>
                )}
              </div>
              <div>
                <div style="font-size:20px;font-weight:700;margin-bottom:3px">{profile.display_name}</div>
                {away[targetId] && <div style="margin-bottom:4px"><AwayBadge away={away[targetId]} /></div>}
                <div style="font-size:13px;color:var(--color-text-muted)">{profile.email}</div>
                <div style="margin-top:6px">
                  <AvailabilityBadge value={profile.availability} />
                </div>
              </div>
            </div>

            {/* Avatar remove */}
            {isOwnProfile && profile.avatar_url && (
              <button
                onClick={handleAvatarDelete}
                style="font-size:12px;color:var(--color-danger,#dc2626);background:none;border:none;cursor:pointer;padding:0;margin-bottom:4px;display:block"
              >
                🗑 Foto verwijderen
              </button>
            )}

            {/* Availability edit */}
            {canEdit && !editingAvail && (
              <button
                onClick={() => setEditingAvail(true)}
                style="font-size:12px;color:var(--color-primary);background:none;border:none;cursor:pointer;padding:0"
              >
                ✏️ Beschikbaarheid wijzigen
              </button>
            )}
            {editingAvail && (
              <div style="margin-top:8px;display:flex;flex-direction:column;gap:8px">
                <select class="form-select" style="font-size:13px" value={availValue} onChange={e => setAvailValue(e.target.value)}>
                  {Object.entries(AVAILABILITY).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
                <div style="display:flex;gap:6px">
                  <Button variant="primary" size="sm" onClick={handleSaveAvailability} disabled={savingAvail}>
                    {savingAvail ? '…' : 'Opslaan'}
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => { setEditingAvail(false); setAvailValue(profile.availability); }}>
                    Annuleren
                  </Button>
                </div>
              </div>
            )}

            {/* Member since */}
            <div style="margin-top:16px;padding-top:12px;border-top:1px solid var(--color-border)">
              <div style="font-size:11px;color:var(--color-text-muted);font-weight:500;text-transform:uppercase;letter-spacing:.04em;margin-bottom:4px">Lid sinds</div>
              <div style="font-size:13px">{formatDate(profile.joined_at || profile.created_at)}</div>
            </div>
          </Card>

          {/* Stats */}
          <Card title="📊 Taakstatistieken">
            <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:8px;text-align:center">
              {[
                { val: profile.stats?.total_tasks ?? 0,     label: 'Totaal' },
                { val: profile.stats?.completed_tasks ?? 0, label: 'Afgerond' },
                { val: profile.stats?.open_tasks ?? 0,      label: 'Open' },
              ].map(s => (
                <div key={s.label} style="background:var(--color-bg-secondary);border-radius:8px;padding:10px 6px;border:1px solid var(--color-border)">
                  <div style="font-size:22px;font-weight:700;color:var(--color-primary)">{s.val}</div>
                  <div style="font-size:11px;color:var(--color-text-muted);margin-top:2px">{s.label}</div>
                </div>
              ))}
            </div>
          </Card>

          {/* Sections */}
          {profile.sections?.length > 0 && (
            <Card title="📂 Secties & functies">
              <div style="display:flex;flex-wrap:wrap;gap:6px">
                {profile.sections.map(s => (
                  <span key={s} style="font-size:12px;padding:3px 10px;border-radius:12px;background:var(--color-primary);color:#fff;font-weight:500">
                    {s}
                  </span>
                ))}
              </div>
            </Card>
          )}

          {/* Roles */}
          {profile.roles?.length > 0 && (
            <Card title="🎭 Rollen">
              <div style="display:flex;flex-direction:column;gap:6px">
                {profile.roles.map(r => (
                  <div key={r.id} style="display:flex;align-items:center;justify-content:space-between;padding:7px 10px;background:var(--color-bg-secondary);border-radius:8px;border:1px solid var(--color-border)">
                    <div>
                      <div style="font-size:13px;font-weight:500">{r.name}</div>
                      {r.description && <div style="font-size:11px;color:var(--color-text-muted)">{r.description}</div>}
                    </div>
                    {r.assigned_at && (
                      <div style="font-size:11px;color:var(--color-text-muted);flex-shrink:0;margin-left:12px">
                        Sinds {parseServerDate(r.assigned_at).toLocaleDateString('nl-NL', { month: 'short', year: 'numeric' })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {isOwnProfile && <PushSettingsCard />}
        </div>

        {/* Right column — bio + certificates + delegation */}
        <div style="display:flex;flex-direction:column;gap:16px">

          {/* Bio card */}
          <Card>
            <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px">
              <div style="font-size:13px;font-weight:700;color:var(--color-text);text-transform:uppercase;letter-spacing:.04em">Over mij</div>
              {isOwnProfile && !editingBio && (
                <button
                  onClick={() => setEditingBio(true)}
                  style="font-size:12px;color:var(--color-primary);background:none;border:none;cursor:pointer;padding:0"
                >
                  ✏️ Bewerken
                </button>
              )}
            </div>

            {editingBio ? (
              <div style="display:flex;flex-direction:column;gap:12px">
                <div>
                  <label style="display:block;font-size:11px;font-weight:600;color:var(--color-text-muted);margin-bottom:4px;text-transform:uppercase;letter-spacing:.04em">Over {profile.display_name.split(' ')[0]}</label>
                  <textarea class="form-input" rows={3} style="resize:vertical" placeholder="Korte beschrijving over jezelf…" value={bioForm.bio} onInput={e => setBioForm(f => ({ ...f, bio: e.target.value }))} />
                  {isOwnProfile && <AiAssist kind="profile_bio" value={bioForm.bio} context={{ functions: (profile.sections || []).join(', ') }} onUse={text => setBioForm(f => ({ ...f, bio: text }))} />}
                </div>
                <div>
                  <label style="display:block;font-size:11px;font-weight:600;color:var(--color-text-muted);margin-bottom:4px;text-transform:uppercase;letter-spacing:.04em">Waarvoor kun je bij mij terecht?</label>
                  <textarea class="form-input" rows={2} style="resize:vertical" placeholder="Bijv. vragen over trainingen, planning…" value={bioForm.contact_for} onInput={e => setBioForm(f => ({ ...f, contact_for: e.target.value }))} />
                  {isOwnProfile && <AiAssist kind="profile_contact_for" value={bioForm.contact_for} context={{ functions: (profile.sections || []).join(', ') }} onUse={text => setBioForm(f => ({ ...f, contact_for: text }))} />}
                </div>
                <div>
                  <label style="display:block;font-size:11px;font-weight:600;color:var(--color-text-muted);margin-bottom:4px;text-transform:uppercase;letter-spacing:.04em">Leuk feitje</label>
                  <input class="form-input" type="text" placeholder="Bijv. al 10 jaar actief bij SV Hoofddorp…" value={bioForm.fun_fact} onInput={e => setBioForm(f => ({ ...f, fun_fact: e.target.value }))} />
                  {isOwnProfile && <AiAssist kind="profile_fun_fact" value={bioForm.fun_fact} onUse={text => setBioForm(f => ({ ...f, fun_fact: text }))} />}
                  <label style="font-size:11px;font-weight:500;color:var(--color-text-muted);display:block;margin-top:2px">Geboortedatum (alleen zichtbaar voor jezelf en beheerders)</label>
                  <input class="form-input" type="date" min="1900-01-01" max={todayIso()} value={bioForm.birth_date} onInput={e => setBioForm(f => ({ ...f, birth_date: e.target.value }))} />
                </div>
                {bioError && <div style="font-size:12px;color:#dc2626">{bioError}</div>}
                <div style="display:flex;gap:6px">
                  <Button variant="primary" size="sm" onClick={handleSaveBio} disabled={savingBio}>{savingBio ? '…' : 'Opslaan'}</Button>
                  <Button variant="secondary" size="sm" onClick={() => { setEditingBio(false); setBioError(null); setBioForm({ bio: profile.bio || '', contact_for: profile.contact_for || '', fun_fact: profile.fun_fact || '', birth_date: profile.birth_date || '' }); }}>Annuleren</Button>
                </div>
              </div>
            ) : (
              <div style="display:flex;flex-direction:column;gap:12px">
                {[
                  { label: `Over ${profile.display_name.split(' ')[0]}`, val: profile.bio },
                  { label: 'Waarvoor kun je bij mij terecht?', val: profile.contact_for },
                  { label: 'Leuk feitje', val: profile.fun_fact },
                ].map(({ label, val }) => (
                  <div key={label}>
                    <div style="font-size:11px;font-weight:600;color:var(--color-text-muted);text-transform:uppercase;letter-spacing:.04em;margin-bottom:3px">{label}</div>
                    <div style={`font-size:13px;color:${val ? 'var(--color-text)' : 'var(--color-text-muted)'};font-style:${val ? 'normal' : 'italic'}`}>
                      {val || (isOwnProfile ? 'Nog niet ingevuld — klik op Bewerken' : '—')}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <ThanksCard key={targetId} userId={targetId} name={profile.display_name} />

          {isOwnProfile && hasModule('handovers') && <HandoverDraftCard />}

          {isOwnProfile && <PasswordCard />}

          {isOwnProfile && <MfaCard />}

          <Card title="🏅 Certificaten & competenties">
            {certs.length === 0 && !showCertForm && (
              <p class="text-muted text-sm" style="margin-bottom:12px">
                {canEdit ? 'Voeg certificaten toe om competenties bij te houden.' : 'Geen certificaten geregistreerd.'}
              </p>
            )}

            {/* All certificates */}
            {certs.length > 0 && (
              <div style="display:flex;flex-direction:column;gap:8px;margin-bottom:12px">
                {certs.map(cert => {
                  const status = cert.status || 'behaald';
                  const sc = STATUS_COLORS[status] || STATUS_COLORS.behaald;
                  const expSoon = status === 'behaald' && isExpiringSoon(cert.expires_date);
                  const isExpiredCert = status === 'behaald' && isExpired(cert.expires_date);
                  const isEditing = editingCertId === cert.id;

                  if (isEditing) {
                    return (
                      <CertificateForm
                        key={cert.id}
                        initial={cert}
                        onSave={form => handleEditCertificate(cert.id, form)}
                        onCancel={() => setEditingCertId(null)}
                        saving={savingCert}
                      />
                    );
                  }

                  return (
                    <div key={cert.id} style={`padding:10px 12px;border-radius:8px;border:1px solid ${expSoon ? '#f59e0b' : sc.border};background:${expSoon ? '#fef9c3' : sc.bg};${isExpiredCert ? 'opacity:0.65' : ''}`}>
                      <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:8px">
                        <div style="flex:1;min-width:0">
                          <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:2px">
                            <div style="font-size:13px;font-weight:600">{cert.name}</div>
                            <span style={`font-size:11px;font-weight:600;padding:2px 8px;border-radius:10px;background:white;border:1px solid ${sc.border};color:${sc.color}`}>
                              {STATUS_LABELS[status]}
                            </span>
                          </div>
                          {cert.issued_by && <div style="font-size:12px;color:var(--color-text-muted)">Uitgegeven door: {cert.issued_by}</div>}
                          {(cert.issued_date || cert.expires_date) && (
                            <div style="font-size:11px;color:var(--color-text-muted);margin-top:3px;display:flex;gap:10px;flex-wrap:wrap">
                              {cert.issued_date && <span>Behaald: {formatDate(cert.issued_date)}</span>}
                              {cert.expires_date && (
                                <span style={expSoon ? 'color:#d97706;font-weight:500' : ''}>
                                  {expSoon ? '⚠️ ' : ''}Geldig t/m: {formatDate(cert.expires_date)}
                                </span>
                              )}
                            </div>
                          )}
                          {/* File attachment — only for Behaald */}
                          {status === 'behaald' && (
                            <div style="margin-top:6px;display:flex;align-items:center;gap:8px;flex-wrap:wrap">
                              {cert.file_path ? (
                                <>
                                  <a href={`/api/profiles/certificates/${cert.id}/file`} target="_blank" rel="noopener noreferrer" onClick={e => fileLinkClick(e, cert.name)} style="font-size:12px;color:var(--color-primary);text-decoration:none">
                                    📎 Bijlage bekijken
                                  </a>
                                  {canEdit && (
                                    <button onClick={() => handleDeleteCertFile(cert.id)} style="font-size:11px;background:none;border:none;cursor:pointer;color:var(--color-text-muted);padding:0">
                                      Verwijder bijlage
                                    </button>
                                  )}
                                </>
                              ) : canEdit && (
                                <label style="font-size:12px;color:var(--color-primary);cursor:pointer">
                                  📎 Bijlage uploaden
                                  <input type="file" accept=".pdf,image/*" style="display:none" onChange={e => handleUploadCertFile(cert.id, e)} />
                                </label>
                              )}
                            </div>
                          )}
                        </div>
                        {canEdit && (
                          <div style="display:flex;gap:4px;flex-shrink:0">
                            <button
                              onClick={() => setEditingCertId(cert.id)}
                              title="Bewerken"
                              style="background:none;border:none;cursor:pointer;color:var(--color-text-muted);font-size:13px;padding:0;line-height:1"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => handleDeleteCertificate(cert.id)}
                              disabled={deletingCertId === cert.id}
                              title="Verwijderen"
                              style="background:none;border:none;cursor:pointer;color:var(--color-text-muted);font-size:13px;padding:0;line-height:1"
                            >
                              🗑
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add certificate */}
            {canEdit && (
              showCertForm ? (
                <CertificateForm
                  onSave={handleAddCertificate}
                  onCancel={() => setShowCertForm(false)}
                  saving={savingCert}
                />
              ) : (
                <button
                  onClick={() => setShowCertForm(true)}
                  style="background:none;border:1px dashed var(--color-border);border-radius:8px;width:100%;padding:8px;color:var(--color-text-muted);font-size:13px;cursor:pointer;text-align:center"
                >
                  + Certificaat toevoegen
                </button>
              )
            )}
          </Card>

          {hasModule('delegation') && <DelegationSection targetId={targetId} canEdit={canEdit} onChange={() => setAwayKey(k => k + 1)} />}
        </div>
      </div>
    </div>
  );
}
